import React, {useCallback, useContext} from 'react';
import {useNavigate} from 'react-router-dom';


import {AccountTeamMemberSettings} from 'src/admin-rights-service/modules/admin-profile/sections/account-team-member-settings';
import {AdminRoleSettings} from 'src/admin-rights-service/modules/admin-profile/sections/admin-role-settings';
import {ConfirmationContext} from 'src/contexts/confirmation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';

import {CreateUserStep} from '../../components/create-user-step/create-user-step';
import {CREATE_USER_STEPS} from '../../constants';

import {UserRoleSettingsController} from './user-role-settings.controller';

import type {ConfirmationContextType} from 'src/contexts/confirmation-context';

type Props = {
    userId: number,
    onBack: () => void,
    canSetupAdminRole: boolean,
    canViewLimits: boolean,
    onRedirectToStep: (step: CREATE_USER_STEPS) => void,
};

export const CreateUserRoleSettingsStep = (props: Props) => {
    const {userId, onBack, canSetupAdminRole, onRedirectToStep, canViewLimits} = props;
    const navigate = useNavigate();
    const {onShowModal} = useContext<ConfirmationContextType>(ConfirmationContext);
    const {userCreateSubmit} = useEventTracking();

    const onCancel = useCallback(() => {
        userCreateSubmit({place: 'User Role Settings', action: 'Cancel'});
        navigate(-1);
    }, [navigate, userCreateSubmit]);

    const onFinish = useCallback(() => {
        userCreateSubmit({place: 'User Role Settings', action: 'Finish'});
        navigate(`/profile/user/${userId}`);
    }, [navigate, userCreateSubmit, userId]);

    return (
        <ValidationContextProvider>
            <UserRoleSettingsController userId={userId} onProceed={onFinish}>
                {({
                    busy,
                    isLoading,
                    loadingError,
                    isChunkChanged,

                    roles,
                    role, changeRole,
                    defaultAdminPermissions,
                    adminPermissions, setAdminPermissions,
                    accountTeamMemberSettings,
                    setAccountTeamMemberSettings,

                    isDefaultAdminPermissions,
                    saveChanges,
                    resetAdminPermissions,
                }) => (
                    <CreateUserStep
                        selectedStep={CREATE_USER_STEPS.ROLE_SETTINGS}
                        onChangeStep={step => {
                            if (isChunkChanged) {
                                onShowModal(() => onRedirectToStep(step));
                            } else {
                                onRedirectToStep(step);
                            }
                        }}
                        loadingError={loadingError}
                        canViewLimits={canViewLimits}
                        canSetupAdminRole={canSetupAdminRole}
                        buttonsBlock={[
                            <Button key="Close" variant="Type.TERTIARY" onClick={onCancel}>Close</Button>,
                            <Button
                                key="Back"
                                variant="Type.SECONDARY"
                                onClick={() => {
                                    if (!isChunkChanged) {
                                        onBack();
                                    } else {
                                        onShowModal(onBack);
                                    }
                                }}>
                                Back
                            </Button>,
                            <Button
                                key="Finish"
                                variant="Type.PRIMARY"
                                busy={busy}
                                onClick={() => saveChanges()}>
                                Finish
                            </Button>,
                        ]}
                        content={(
                            <Group gap="l" direction="v" alignItems="start">
                                <AccountTeamMemberSettings {...{
                                    isLoading,
                                    accountTeamMemberSettings,
                                    setAccountTeamMemberSettings,
                                }}
                                />
                                <AdminRoleSettings {...{
                                    isLoading,
                                    role,
                                    roles,
                                    isDefaultAdminPermissions,
                                    resetAdminPermissions,
                                    changeRole,
                                    adminPermissions,
                                    defaultAdminPermissions,
                                    setAdminPermissions,
                                }}
                                />
                            </Group>
                        )}
                    />
                )}
            </UserRoleSettingsController>
        </ValidationContextProvider>
    );
};
